import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import SkipToContent from './components/SkipToContent';
import HeroSection from './components/HeroSection';
import AISection from './components/AISection';
import AgencySection from './components/AgencySection';
import CaseStudiesSection from './components/CaseStudiesSection';
import TestimonialsSection from './components/TestimonialsSection';
import PricingSection from './components/PricingSection';
import BlogSection from './components/BlogSection';
import BlogPost from './components/BlogPost';
import Footer from './components/Footer';
import { getCurrentUser } from './lib/auth';
import { User, Tab } from './lib/types';
import Dashboard from './components/Dashboard';
import PimperPage from './components/PimperPage';
import AdminUsers from './pages/AdminUsers';
import AdminCatalogue from './pages/AdminCatalogue';
import AdminSEO from './pages/AdminSEO';
import ResourcesPage from './pages/ResourcesPage';
import ArticlePage from './pages/ArticlePage';
import ArticleEditor from './components/ArticleEditor';
import SubscriptionPage from './pages/SubscriptionPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';
import UserProfile from './components/UserProfile';
import JobPostGenerator from './components/JobPostGenerator';
import SettingsPanel from './components/SettingsPanel';
import InboxPage from './components/InboxPage';
import MessageWidget from './components/MessageWidget';
import CalendarPage from './pages/CalendarPage';

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('account');

  useEffect(() => {
    const checkUser = async () => {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
    };
    checkUser();
  }, []);

  useEffect(() => {
    const handleStorageChange = async (e: StorageEvent) => {
      if (e.key === 'sb-liytabsqvlimhkmtjxdv-auth-token') {
        const currentUser = await getCurrentUser();
        setUser(currentUser);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const handleLoginSuccess = async (loggedInUser: User) => {
    setUser(loggedInUser);
    setActiveTab('account');
  };

  const handleLogout = () => {
    setUser(null);
  };

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
  };

  const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
    if (!user) {
      return <Navigate to="/" replace />;
    }
    return <>{children}</>;
  };

  const AdminRoute = ({ children }: { children: React.ReactNode }) => {
    if (!user || user.role !== 'admin') {
      return <Navigate to="/" replace />;
    }
    return <>{children}</>;
  };

  const LandingPage = () => (
    <>
      <HeroSection />
      <AISection />
      <AgencySection />
      <CaseStudiesSection />
      <TestimonialsSection />
      <PricingSection />
      <BlogSection />
      <Footer />
    </>
  );

  return (
    <Router>
      <div className="min-h-screen flex flex-col">
        <Helmet defaultTitle="PimpMyJob - Générateur d'offres d'emploi IA">
          <html lang="fr" />
          <meta name="description" content="Créez des offres d'emploi optimisées avec l'IA. Attirez les meilleurs talents avec des annonces professionnelles générées en quelques clics." />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        </Helmet>
        
        <SkipToContent />
        <Navbar user={user} onLoginSuccess={handleLoginSuccess} onLogout={handleLogout} />
        
        <main id="main-content" className="flex-1 pt-16">
          <Routes>
            {/* Public routes */}
            <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <LandingPage />} />
            <Route path="/blog/:id" element={<BlogPost />} />
            <Route path="/resources" element={<ResourcesPage />} />
            <Route path="/resources/:slug" element={<ArticlePage />} />
            
            {/* Protected routes with shared layout */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <div className="flex min-h-screen">
                  <Sidebar activeTab={activeTab} onTabChange={handleTabChange} user={user} />
                  <div className="flex-1 p-6 bg-gray-50">
                    <Dashboard 
                      user={user!} 
                      onLogout={handleLogout} 
                      activeTab={activeTab} 
                      onTabChange={handleTabChange} 
                    />
                  </div>
                </div>
              </ProtectedRoute>
            } />
            
            <Route path="/account" element={
              <ProtectedRoute>
                <div className="flex min-h-screen">
                  <Sidebar activeTab="account" onTabChange={handleTabChange} user={user} />
                  <div className="flex-1 p-6 bg-gray-50">
                    <UserProfile user={user!} onLogout={handleLogout} />
                  </div>
                </div>
              </ProtectedRoute>
            } />

            <Route path="/settings" element={
              <ProtectedRoute>
                <div className="flex min-h-screen">
                  <Sidebar activeTab="setup" onTabChange={handleTabChange} user={user} />
                  <div className="flex-1 p-6 bg-gray-50">
                    <SettingsPanel user={user!} />
                  </div>
                </div>
              </ProtectedRoute>
            } />
            
            <Route path="/pimper" element={
              <ProtectedRoute>
                <div className="flex min-h-screen">
                  <Sidebar activeTab="pimper" onTabChange={handleTabChange} user={user} />
                  <div className="flex-1 p-6 bg-gray-50">
                    <PimperPage 
                      user={user!} 
                      activeTab="pimper" 
                      onTabChange={handleTabChange} 
                    />
                  </div>
                </div>
              </ProtectedRoute>
            } />

            <Route path="/générer" element={
              <ProtectedRoute>
                <div className="flex min-h-screen">
                  <Sidebar activeTab="write" onTabChange={handleTabChange} user={user} />
                  <div className="flex-1 p-6 bg-gray-50">
                    <JobPostGenerator user={user!} />
                  </div>
                </div>
              </ProtectedRoute>
            } />

            <Route path="/calendar" element={
              <ProtectedRoute>
                <CalendarPage />
              </ProtectedRoute>
            } />

            {/* Admin-only routes */}
            <Route path="/resources/new" element={
              <AdminRoute>
                <div className="flex min-h-screen">
                  <Sidebar activeTab="learn" onTabChange={handleTabChange} user={user} />
                  <div className="flex-1 p-6 bg-gray-50">
                    <ArticleEditor />
                  </div>
                </div>
              </AdminRoute>
            } />

            <Route path="/resources/edit/:slug" element={
              <AdminRoute>
                <div className="flex min-h-screen">
                  <Sidebar activeTab="learn" onTabChange={handleTabChange} user={user} />
                  <div className="flex-1 p-6 bg-gray-50">
                    <ArticleEditor />
                  </div>
                </div>
              </AdminRoute>
            } />
            
            <Route path="/subscription" element={
              <ProtectedRoute>
                <div className="flex min-h-screen">
                  <Sidebar activeTab="bank" onTabChange={handleTabChange} user={user} />
                  <div className="flex-1 p-6 bg-gray-50">
                    <PricingSection />
                  </div>
                </div>
              </ProtectedRoute>
            } />
            
            <Route path="/subscription/success" element={
              <ProtectedRoute>
                <SubscriptionSuccessPage />
              </ProtectedRoute>
            } />

            <Route path="/inbox" element={
              <ProtectedRoute>
                <div className="flex min-h-screen">
                  <Sidebar activeTab="inbox" onTabChange={handleTabChange} user={user} />
                  <div className="flex-1">
                    <InboxPage />
                  </div>
                </div>
              </ProtectedRoute>
            } />

            {/* Admin routes */}
            <Route path="/admin/users" element={
              <AdminRoute>
                <div className="flex min-h-screen">
                  <Sidebar activeTab="users" onTabChange={handleTabChange} user={user} />
                  <div className="flex-1">
                    <AdminUsers />
                  </div>
                </div>
              </AdminRoute>
            } />
            
            <Route path="/admin/catalogue" element={
              <AdminRoute>
                <div className="flex min-h-screen">
                  <Sidebar activeTab="catalogue" onTabChange={handleTabChange} user={user} />
                  <div className="flex-1 p-6 bg-gray-50">
                    <AdminCatalogue />
                  </div>
                </div>
              </AdminRoute>
            } />

            <Route path="/admin/seo" element={
              <AdminRoute>
                <div className="flex min-h-screen">
                  <Sidebar activeTab="setup" onTabChange={handleTabChange} user={user} />
                  <div className="flex-1 p-6 bg-gray-50">
                    <AdminSEO />
                  </div>
                </div>
              </AdminRoute>
            } />

            {/* Fallback route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>

        {/* Message Widget */}
        <MessageWidget />
      </div>
    </Router>
  );
}

export default App;